import { Clock, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { contactService } from '../../../shared/services/contactService';

type ContactFormState = {
  fullname: string;
  telephone: string;
  email: string;
  subject: string;
  body: string;
  accepted: boolean;
};

const SUPPORT_EMAIL = 'support@haapa.store';

const inputClassName =
  'mt-2 w-full rounded-xl border border-slate-200 bg-white dark:bg-[#0b1220] dark:border-slate-800 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 shadow-sm outline-none transition focus:border-orange-400 dark:focus:border-orange-500 focus:ring-2 focus:ring-orange-200/70 dark:focus:ring-orange-900/30';

const infoCardClassName =
  'flex items-start gap-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b1220] p-4 shadow-sm';

const ContactPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');
  const [form, setForm] = useState<ContactFormState>({
    fullname: '',
    telephone: '',
    email: '',
    subject: '',
    body: '',
    accepted: false,
  });

  const mailtoHref = useMemo(() => {
    const subject = form.subject.trim() || 'Haapa Store - Contact request';
    const lines = [
      `Full name: ${form.fullname.trim() || '-'}`,
      `Telephone: ${form.telephone.trim() || '-'}`,
      `Email: ${form.email.trim() || '-'}`,
      '',
      form.body.trim() || '-',
    ];
    const body = lines.join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [form.body, form.email, form.fullname, form.subject, form.telephone]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.accepted) return;

    setStatus('sending');
    setFeedback('');
    try {
      const result = await contactService.send({
        fullname: form.fullname,
        telephone: form.telephone,
        email: form.email,
        subject: form.subject,
        body: form.body,
      });

      if (result.success) {
        setStatus('sent');
        setFeedback(result.message);
        setForm((prev) => ({ ...prev, subject: '', body: '' }));
        return;
      }

      setStatus('error');
      setFeedback(result.message);
      if (result.message.toLowerCase().includes('not found')) {
        window.location.href = mailtoHref;
      }
    } catch (error: unknown) {
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Unable to send your message.');
    }
  };

  return (
    <section className="bg-[#fffaf7] dark:bg-[#070b16] pt-24">
      <section className="relative isolate overflow-hidden bg-white dark:bg-[#0b1220]">
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0 [background-image:radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.20),transparent_52%),radial-gradient(circle_at_85%_10%,rgba(83,185,193,0.18),transparent_52%),radial-gradient(circle_at_55%_90%,rgba(251,146,60,0.12),transparent_60%)]"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-[0.45] [background-image:radial-gradient(rgba(15,23,42,0.12)_1px,transparent_1px)] [background-size:18px_18px]"
            aria-hidden="true"
          />

          <div className="pointer-events-none absolute -left-32 top-24 h-[520px] w-[520px] rounded-full bg-orange-400/20 blur-3xl haapa-glow-pulse" />
          <div className="pointer-events-none absolute -right-40 top-10 h-[560px] w-[560px] rounded-full bg-iw-teal/18 blur-3xl haapa-glow-pulse [animation-delay:1.4s]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 py-20 text-center text-slate-900 dark:text-slate-100 sm:px-6 lg:px-8">
          <p className="mx-auto inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-700 dark:text-orange-500 shadow-sm backdrop-blur">
            {t('contact.overline')}
          </p>
          <h1 className="mt-6 text-4xl font-semibold leading-tight md:text-6xl">{t('contact.title')}</h1>
          <p className="mx-auto mt-4 max-w-3xl text-sm text-slate-600 dark:text-slate-400 sm:text-base">{t('contact.subtitle')}</p>
        </div>
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-[#fffaf7] dark:bg-[#070b16] [clip-path:polygon(0,55%,100%,0,100%,100%,0,100%)]"
          aria-hidden="true"
        />
      </section>

      <section className="relative mx-auto -mt-16 max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-orange-100 dark:border-slate-800 bg-white dark:bg-[#0b1220] p-6 shadow-[0_30px_90px_-60px_rgba(15,23,42,0.6)] sm:p-8"
          >
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{t('contact.formTitle')}</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{t('contact.formSubtitle')}</p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {t('contact.fields.name')}
                <input
                  value={form.fullname}
                  onChange={(event) => setForm((prev) => ({ ...prev, fullname: event.target.value }))}
                  placeholder={t('contact.placeholders.name')}
                  className={inputClassName}
                  autoComplete="name"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 sm:col-span-2">
                {t('contact.fields.telephone')}
                <input
                  value={form.telephone}
                  onChange={(event) => setForm((prev) => ({ ...prev, telephone: event.target.value }))}
                  placeholder={t('contact.placeholders.telephone')}
                  className={inputClassName}
                  autoComplete="tel"
                  inputMode="tel"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 sm:col-span-2">
                {t('contact.fields.email')}
                <input
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder={t('contact.placeholders.email')}
                  className={inputClassName}
                  autoComplete="email"
                  inputMode="email"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 sm:col-span-2">
                {t('contact.fields.subject')}
                <input
                  value={form.subject}
                  onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                  placeholder={t('contact.placeholders.subject')}
                  className={inputClassName}
                />
              </label>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 sm:col-span-2">
                {t('contact.fields.message')}
                <textarea
                  value={form.body}
                  onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
                  placeholder={t('contact.placeholders.message')}
                  className={`${inputClassName} min-h-[180px] resize-y`}
                />
              </label>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={form.accepted}
                  onChange={(event) => setForm((prev) => ({ ...prev, accepted: event.target.checked }))}
                  className="h-4 w-4 accent-orange-600"
                />
                <span>
                  {t('contact.privacyPrefix')}{' '}
                  <Link to="/privacy-policy" className="text-orange-700 dark:text-orange-500 hover:text-orange-600">
                    {t('contact.privacyLink')}
                  </Link>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={!form.accepted || status === 'sending'}
                className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-white transition ${
                  form.accepted && status !== 'sending'
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'cursor-not-allowed bg-slate-300 dark:bg-slate-800 dark:text-slate-500'
                }`}
              >
                {status === 'sending'
                  ? t('contact.sendingCta')
                  : status === 'sent'
                    ? t('contact.sentCta')
                    : t('contact.sendCta')}
              </button>
            </div>

            {feedback && (
              <p
                className={`mt-4 rounded-2xl border px-4 py-3 text-xs font-semibold ${
                  status === 'sent'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : status === 'error'
                      ? 'border-rose-200 bg-rose-50 text-rose-700'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                {feedback}
              </p>
            )}

            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
              {t('contact.altContact')}{' '}
              <a className="font-semibold text-orange-700 dark:text-orange-500 hover:text-orange-600" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </form>

          <div className="space-y-6">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-orange-100 dark:bg-orange-950/30 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-700 dark:text-orange-500">
                {t('contact.infoTag')}
              </p>
              <h2 className="mt-4 text-3xl font-semibold text-slate-900 dark:text-slate-100">{t('contact.infoTitle')}</h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{t('contact.infoSubtitle')}</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className={infoCardClassName}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-500">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('contact.cards.officeTitle')}</p>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('contact.cards.officeValue')}</p>
                </div>
              </div>

              <div className={infoCardClassName}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-500">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('contact.cards.phoneTitle')}</p>
                  <a
                    className="mt-1 block text-sm font-semibold text-orange-700 dark:text-orange-500 hover:text-orange-600"
                    href={`tel:${t('contact.ctaPhoneDial')}`}
                  >
                    {t('contact.cards.phoneValue')}
                  </a>
                </div>
              </div>

              <div className={infoCardClassName}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-500">
                  <Clock className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('contact.cards.hoursTitle')}</p>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('contact.cards.hoursValue')}</p>
                </div>
              </div>

              <div className={infoCardClassName}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-500">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('contact.cards.emailTitle')}</p>
                  <a className="mt-1 block text-sm font-semibold text-orange-700 dark:text-orange-500 hover:text-orange-600" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b1220] p-6 shadow-sm">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('contact.socialTitle')}</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('contact.socialSubtitle')}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  { icon: Facebook, label: 'Facebook' },
                  { icon: Twitter, label: 'Twitter' },
                  { icon: Linkedin, label: 'LinkedIn' },
                  { icon: Instagram, label: 'Instagram' },
                ].map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className="inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/80 p-3 text-slate-700 dark:text-slate-300 shadow-sm transition hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:hover:border-orange-900/50 dark:hover:bg-orange-950/20 dark:hover:text-orange-500"
                    aria-label={item.label}
                  >
                    <item.icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden bg-white dark:bg-[#0b1220]">
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0 [background-image:radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.20),transparent_52%),radial-gradient(circle_at_85%_10%,rgba(83,185,193,0.18),transparent_52%),radial-gradient(circle_at_55%_90%,rgba(251,146,60,0.12),transparent_60%)]"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-[0.45] [background-image:radial-gradient(rgba(15,23,42,0.12)_1px,transparent_1px)] [background-size:18px_18px]"
            aria-hidden="true"
          />

          <div className="pointer-events-none absolute -left-32 top-24 h-[520px] w-[520px] rounded-full bg-orange-400/20 blur-3xl haapa-glow-pulse" />
          <div className="pointer-events-none absolute -right-40 top-10 h-[560px] w-[560px] rounded-full bg-iw-teal/18 blur-3xl haapa-glow-pulse [animation-delay:1.4s]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 py-16 text-center text-slate-900 dark:text-slate-100 sm:px-6 lg:px-8">
          <p className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-700 dark:text-orange-500 shadow-sm backdrop-blur">
            {t('contact.ctaBadge')}
          </p>
          <h2 className="mt-5 text-4xl font-semibold md:text-5xl">{t('contact.ctaTitle')}</h2>
          <p className="mx-auto mt-3 max-w-3xl text-sm text-slate-600 dark:text-slate-400">{t('contact.ctaSubtitle')}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${t('contact.ctaPhoneDial')}`}
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-orange-700"
            >
              <Phone className="h-4 w-4" />
              {t('contact.ctaPhone')}
            </a>
            <Link
              to="/books"
              className="inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100 shadow-sm backdrop-blur transition hover:bg-white dark:hover:bg-slate-800"
            >
              {t('contact.ctaSecondary')}
            </Link>
          </div>
        </div>
      </section>
    </section>
  );
};

export default ContactPage;
